// ============================================
// Localization System — replaces LocalizationManager.cs
// TSV-based: key\tvalue per line. 5 languages.
// ============================================

import { events, Events } from '../engine/events.js';
import { COMMON_TRANSLATIONS } from './common-translations.js';
import { fetchWithEdgeRetry } from '../engine/story-fetch.js';

function _escapeHtml(str) {
    if (typeof str !== 'string') return String(str);
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Walk an EN template + the Ink-resolved text side-by-side to recover the
 * runtime values of Ink `~ temp` variables and the truthiness of Ink
 * conditional expressions. Used by `translateLine()` so translated strings
 * containing `{flirt_r}`, `${bonus_tips}`, `{Energy < 20: energy}` etc.
 * resolve correctly without re-running Ink.
 *
 * Handled token shapes:
 *   - `${var}`       dollar-prefixed number (salon prices, money totals)
 *   - `{var}`        plain identifier substitution (streaming temp vars
 *                    like `{flirt_r}`, `{bonus_viewers}`)
 *   - `{func()}`     Ink/L10n-resolved function call, skipped on extract
 *                    and resolved at interpolate-time via registered _funcs
 *   - `{cond: body}` conditional; truthiness recorded in `_conditions`
 */
/**
 * Walk a `{cond:body}` body against a resolved-slice. Body may contain
 * `${var}` (price interpolation, captured as `$N`) or `{var}` (plain
 * value, captured as whatever sits in the gap up to the next literal).
 * Returns `{ truthy, consumed, captured: [{name,value}] }`.
 *
 * Truthy when every literal piece of body appears in `slice` in order.
 * `consumed` is the byte count of slice that was matched (advances pos
 * in the outer walker so the next segment starts after the cond body).
 */
function _matchCondBody(body, slice) {
    const fail = { truthy: false, consumed: 0, captured: [] };
    if (!body) return fail;
    // Tokenize body into [lit, dollarvar, var] pieces
    const re = /\$\{([a-zA-Z_][a-zA-Z0-9_]*)\}|\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    const pieces = [];
    let bl = 0;
    let bm;
    while ((bm = re.exec(body)) !== null) {
        if (bm.index > bl) pieces.push({ type: 'lit', text: body.slice(bl, bm.index) });
        if (bm[1] !== undefined) pieces.push({ type: 'dollarvar', name: bm[1] });
        else if (bm[2] !== undefined) pieces.push({ type: 'var', name: bm[2] });
        bl = bm.index + bm[0].length;
    }
    if (bl < body.length) pieces.push({ type: 'lit', text: body.slice(bl) });

    // Pure-literal fast path: indexOf the whole body
    if (pieces.length === 1 && pieces[0].type === 'lit') {
        const at = slice.indexOf(pieces[0].text);
        return at < 0 ? fail : { truthy: true, consumed: at + pieces[0].text.length, captured: [] };
    }

    // Walk pieces against slice. For each var-piece, capture the gap up to
    // the next literal (or end-of-slice for trailing vars).
    let cursor = 0;
    const captured = [];
    for (let p = 0; p < pieces.length; p++) {
        const piece = pieces[p];
        if (piece.type === 'lit') {
            if (!piece.text) continue;
            const at = slice.indexOf(piece.text, cursor);
            if (at < 0) return fail;
            // Resolve pending capture(s) from the gap
            for (let q = captured.length - 1; q >= 0 && captured[q].value === undefined; q--) {
                const gap = slice.slice(cursor, at);
                if (captured[q].type === 'dollarvar') {
                    const dm = gap.match(/^\$(\d+(?:\.\d+)?)/);
                    if (dm) captured[q].value = dm[1];
                } else {
                    captured[q].value = gap;
                }
                break;
            }
            cursor = at + piece.text.length;
        } else {
            captured.push({ type: piece.type, name: piece.name, value: undefined });
        }
    }
    // Trailing var-piece with no anchor: consume rest of slice
    if (captured.length) {
        const last = captured[captured.length - 1];
        if (last.value === undefined) {
            const tail = slice.slice(cursor);
            if (last.type === 'dollarvar') {
                const dm = tail.match(/^\$(\d+(?:\.\d+)?)/);
                if (dm) { last.value = dm[1]; cursor += dm[0].length; }
            } else {
                last.value = tail;
                cursor += tail.length;
            }
        }
    }
    return { truthy: true, consumed: cursor, captured };
}

function _extractInkTempVars(template, resolved) {
    if (!template || !resolved) return null;
    const out = {};
    const conditions = new Map();
    let used = false;
    // Cond body allows `${var}` and `{var}` tokens inline
    // (e.g. `{not visited:Need ${price} and 15 Energy}`, `{cond:{counter} days left}`).
    const tokenRe = /\$\{([a-zA-Z_][a-zA-Z0-9_]*)\}|\{([a-z_][a-z_0-9]*)\(\)\}|\{([^{}]+?):\s*((?:[^{}]|\$\{[a-zA-Z_][a-zA-Z0-9_]*\}|\{[a-zA-Z_][a-zA-Z0-9_]*\})*?)\}|\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    const segments = [];
    let last = 0;
    let m;
    while ((m = tokenRe.exec(template)) !== null) {
        if (m.index > last) segments.push({ type: 'lit', text: template.slice(last, m.index) });
        if (m[1]) segments.push({ type: 'dollarvar', name: m[1] });
        else if (m[2]) segments.push({ type: 'func' });
        else if (m[3]) segments.push({ type: 'cond', expr: m[3].trim(), body: m[4] });
        else if (m[5]) segments.push({ type: 'var', name: m[5] });
        last = m.index + m[0].length;
    }
    if (last < template.length) segments.push({ type: 'lit', text: template.slice(last) });

    let pos = 0;
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        if (seg.type === 'lit') {
            const idx = resolved.indexOf(seg.text, pos);
            if (idx < 0) return null;
            pos = idx + seg.text.length;
        } else if (seg.type === 'dollarvar') {
            const m2 = resolved.slice(pos).match(/^\$(\d+(?:\.\d+)?)/);
            if (!m2) continue;
            out[seg.name] = m2[1];
            pos += m2[0].length;
            used = true;
        } else if (seg.type === 'func') {
            // Skip past the func's rendered value; next literal segment is the anchor.
            const next = segments[i + 1];
            if (next && next.type === 'lit') {
                const stop = resolved.indexOf(next.text, pos);
                if (stop >= 0) pos = stop;
            }
        } else if (seg.type === 'var') {
            // Capture the rendered identifier between current pos and next anchor.
            const next = segments[i + 1];
            const stop = (next && next.type === 'lit')
                ? resolved.indexOf(next.text, pos)
                : resolved.length;
            if (stop < 0) continue;
            out[seg.name] = resolved.slice(pos, stop);
            pos = stop;
            used = true;
        } else { // cond
            const next = segments[i + 1];
            const stopAt = (next && next.type === 'lit')
                ? resolved.indexOf(next.text, pos)
                : resolved.length;
            const slice = resolved.slice(pos, stopAt < 0 ? resolved.length : stopAt);
            const body = (seg.body || '').trim();
            const { truthy, consumed, captured } = _matchCondBody(body, slice);
            if (truthy) {
                for (const c of captured) {
                    if (c.value !== undefined) out[c.name] = c.value;
                }
                pos += consumed;
            }
            conditions.set(seg.expr, !!truthy);
            used = true;
        }
    }
    if (!used) return null;
    if (conditions.size > 0) out._conditions = conditions;
    return out;
}

const LANG_KEY = 'PnO_Language';
const TSV_BASE = 'assets/data/localization/';

class Localization {
    constructor() {
        /** @type {string} Current language code */
        this._lang = 'en';
        /** @type {Map<string, Map<string, string>>} lang -> key -> translation */
        this._data = new Map();
        /** @type {boolean} Whether init has been called */
        this._loaded = false;
        /** @type {function(string): *} Ink variable resolver (set by main.js after init) */
        this._getVar = null;
        /** @type {Map<string, function>} Ink function resolvers for {func()} patterns */
        this._funcs = new Map();

        try {
            const saved = localStorage.getItem(LANG_KEY);
            if (saved) this._lang = saved;
        } catch(e) {}
    }

    /**
     * Load English + active language TSV files.
     * Call once during app init.
     */
    async init() {
        if (this._loaded) return;
        this._loaded = true;

        // Load English + active language in parallel
        const fetches = [this._fetchTSV('en')];
        if (this._lang !== 'en') {
            fetches.push(this._fetchTSV(this._lang));
        }
        await Promise.all(fetches);

        console.log(`[L10n] Loaded: en (${this._data.get('en')?.size || 0} keys)` +
            (this._lang !== 'en' ? `, ${this._lang} (${this._data.get(this._lang)?.size || 0} keys)` : ''));

        // Notify listeners so static [data-l10n] elements that were rendered
        // with English fallback text get translated to the saved language.
        // Without this, the main menu stays English on first paint after a
        // restart even though localStorage.PnO_Language is e.g. "es" — user
        // had to toggle the language picker once for the refresh to fire.
        if (this._lang !== 'en') {
            events.emit(Events.LOCALE_CHANGED || 'locale:changed', this._lang);
        }
    }

    /** Get translation for key. Falls back to common translations, then English,
     *  then the optional `fallback` arg (EN literal at call site), then the key itself. */
    get(key, fallback) {
        if (this._lang !== 'en') {
            // 1. Check language-specific TSV (skip empty values so EN fallback wins)
            const langMap = this._data.get(this._lang);
            if (langMap && langMap.has(key)) {
                const v = langMap.get(key);
                if (v) return v;
            }

            // 2. Check common translations (deduplicated shared strings)
            const enMap = this._data.get('en');
            const enVal = enMap?.get(key);
            if (enVal && COMMON_TRANSLATIONS[enVal]?.[this._lang]) {
                return COMMON_TRANSLATIONS[enVal][this._lang];
            }
        }
        const enMap = this._data.get('en');
        if (enMap && enMap.has(key)) return enMap.get(key);
        if (fallback !== undefined) return fallback;
        return key;
    }

    /** Get SMS translation. Key format: "npcId|englishText" */
    getSMS(npcId, englishText) {
        const key = `${npcId}|${englishText}`;
        return this.get(key);
    }

    /** Get item translation. Key format: "item.{tag}.{field}" */
    getItem(tag, field, fallback) {
        const key = `item.${tag}.${field}`;
        const result = this.get(key);
        return result === key ? (fallback || key) : result;
    }

    /**
     * Set the Ink variable resolver function.
     * Called once from main.js after variableManager is initialized.
     * @param {function(string): *} fn - takes varName, returns value or undefined
     */
    setVariableResolver(fn) {
        this._getVar = fn;
    }

    /**
     * Register an Ink function for interpolation (e.g. get_viewer_name).
     * @param {string} name - function name without parens
     * @param {function(): *} fn - zero-arg function returning a value
     */
    registerFunction(name, fn) {
        this._funcs.set(name, fn);
    }

    /**
     * Interpolate Ink variable references in translated text.
     * Handles {VarName}, ${VarName}, and {func()} patterns.
     * Strips Ink conditional expressions like {condition: text}.
     * Handles Ink alternative syntax { var: text_a | text_b }.
     * @param {string} text
     * @returns {string}
     */
    interpolate(text, ctx = null) {
        if (!text || !this._getVar) return text;
        // Skip if no curly braces at all
        if (text.indexOf('{') < 0) return text;

        // Resolver: variableManager first, then ctx fallback (for Ink temp vars
        // that don't live in variablesState — e.g. salon prices declared via
        // `~ temp laser_price = 75`).
        const resolve = (name) => {
            const v = this._getVar(name);
            if (v !== undefined && v !== null) return v;
            if (ctx && ctx[name] !== undefined && ctx[name] !== null) return ctx[name];
            return undefined;
        };

        // Replace {func()} Ink function calls (e.g. {get_viewer_name()})
        text = text.replace(/\{([a-z_][a-z_0-9]*)\(\)\}/g, (match, funcName) => {
            const fn = this._funcs.get(funcName);
            if (fn) return _escapeHtml(String(fn()));
            return match;
        });

        // Handle Ink conditional alternative: { var: text_a | text_b }
        // Shows text_a if var is truthy, text_b otherwise
        text = text.replace(/\{\s*([a-zA-Z_][a-zA-Z_0-9]*)\s*:\s*((?:[^|}])*?)\s*\|\s*((?:[^}])*?)\s*\}/g, (match, varName, textTrue, textFalse) => {
            const val = resolve(varName);
            return val ? textTrue.trim() : textFalse.trim();
        });

        // Handle Ink conditional switch: { var: text - else: text }
        text = text.replace(/\{\s*([a-zA-Z_][a-zA-Z_0-9]*)\s*:\s*((?:[^}])*?)\s*-\s*else\s*:\s*((?:[^}])*?)\s*\}/g, (match, varName, textTrue, textFalse) => {
            const val = resolve(varName);
            return val ? textTrue.trim() : textFalse.trim();
        });

        // Evaluate Ink comparison conditionals: { var COMP value: text }
        // and compound { var1 COMP val1 && var2 COMP val2: text }
        // Examples: {Energy < 20: energy} → "energy" if Energy < 20, else ""
        //           {Energy < 20 && Money < laser_price: /} → "/" if both true
        // Falls back to deletion when an operand can't be resolved.
        // Body allows `${var}` tokens inline (matched on disabled-reason templates
        // like `{not visited:Need ${price} and 15 Energy}`).
        const compareRe = /\{\s*((?:not\s+)?[a-zA-Z_][a-zA-Z0-9_]*(?:\s*[<>=!]+\s*[a-zA-Z_0-9]+)?(?:\s*&&\s*[a-zA-Z_][a-zA-Z0-9_]*(?:\s*[<>=!]+\s*[a-zA-Z_0-9]+)?)*)\s*:\s*((?:[^{}]|\$\{[a-zA-Z_][a-zA-Z0-9_]*\})*)\}/g;
        const evalOperand = (op) => {
            op = op.trim();
            if (/^-?\d+(?:\.\d+)?$/.test(op)) return Number(op);
            const v = resolve(op);
            if (v === undefined) return undefined;
            return typeof v === 'boolean' ? (v ? 1 : 0) : v;
        };
        const evalAtom = (atom) => {
            atom = atom.trim();
            // `not var` → !truthy(var)
            const notMatch = atom.match(/^not\s+([a-zA-Z_][a-zA-Z0-9_]*)$/);
            if (notMatch) {
                const v = resolve(notMatch[1]);
                return v === undefined ? null : !v;
            }
            // `var OP value`
            const cmp = atom.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*(<=|>=|==|!=|<|>)\s*(.+)$/);
            if (cmp) {
                const lhs = evalOperand(cmp[1]);
                const rhs = evalOperand(cmp[3]);
                if (lhs === undefined || rhs === undefined) return null;
                switch (cmp[2]) {
                    case '<':  return lhs <  rhs;
                    case '<=': return lhs <= rhs;
                    case '>':  return lhs >  rhs;
                    case '>=': return lhs >= rhs;
                    case '==': return lhs == rhs;
                    case '!=': return lhs != rhs;
                }
                return null;
            }
            // bare `var` → truthy check
            const bare = atom.match(/^([a-zA-Z_][a-zA-Z0-9_]*)$/);
            if (bare) {
                const v = resolve(bare[1]);
                return v === undefined ? null : !!v;
            }
            return null;
        };
        text = text.replace(compareRe, (match, expr, body) => {
            // Side-channel: if EN-template extraction already evaluated this
            // exact condition expression against c.originalText, trust that.
            // Handles compounds whose operands include unresolvable Ink temp
            // vars (e.g. `Money < laser_price`).
            if (ctx && ctx._conditions instanceof Map && ctx._conditions.has(expr.trim())) {
                return ctx._conditions.get(expr.trim()) ? body : '';
            }
            const atoms = expr.split(/\s*&&\s*/);
            let allTrue = true;
            for (const a of atoms) {
                const r = evalAtom(a);
                if (r === null) return ''; // unresolvable → drop the whole block
                if (!r) { allTrue = false; break; }
            }
            return allTrue ? body : '';
        });

        // Evaluate `{expr}` arithmetic like `{spa_body_cooldown_day + 7 - Day}`
        // OR `{5 - laser_sessions}` (leading numeric literal). Identifiers
        // resolved via _getVar; numeric literals taken as-is. Used in
        // disabled-reason templates ("Recovering... ({var + 7 - Day}d)") where
        // Ink resolves at runtime — the L10n side rebuilds the same value
        // here. Parenthesised sub-expressions are NOT supported by this
        // simple left-to-right evaluator; rewrite Ink expressions
        // algebraically to flat form (e.g. `3 - (Day - x)` → `x + 3 - Day`).
        const arithRe = /\{([a-zA-Z_0-9][a-zA-Z0-9_]*(?:\s*[+\-*/]\s*[a-zA-Z_0-9]+)+)\}/g;
        text = text.replace(arithRe, (match, expr) => {
            const tokens = expr.split(/\s*([+\-*/])\s*/);
            const evalOp = (t) => {
                if (/^-?\d+(?:\.\d+)?$/.test(t)) return Number(t);
                const v = resolve(t);
                if (v === undefined || v === null) return null;
                return typeof v === 'boolean' ? (v ? 1 : 0) : Number(v);
            };
            let acc = evalOp(tokens[0]);
            if (acc === null || Number.isNaN(acc)) return match;
            for (let i = 1; i < tokens.length; i += 2) {
                const rhs = evalOp(tokens[i + 1]);
                if (rhs === null || Number.isNaN(rhs)) return match;
                switch (tokens[i]) {
                    case '+': acc += rhs; break;
                    case '-': acc -= rhs; break;
                    case '*': acc *= rhs; break;
                    case '/': acc = rhs === 0 ? acc : acc / rhs; break;
                }
            }
            return _escapeHtml(String(acc));
        });

        // Replace ${VarName} (dollar-prefixed variables, common for money values)
        text = text.replace(/\$\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (match, varName) => {
            const val = resolve(varName);
            return (val !== undefined && val !== null) ? `$${_escapeHtml(String(val))}` : match;
        });

        // Replace {VarName} — match names starting with a letter/underscore
        // Avoid matching {0}, {1} etc. (C# format placeholders used by UI L10n)
        text = text.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (match, varName) => {
            const val = resolve(varName);
            return (val !== undefined && val !== null) ? _escapeHtml(String(val)) : match;
        });

        // Clean up double spaces left by removed conditionals
        text = text.replace(/  +/g, ' ').trim();

        return text;
    }

    /**
     * Translate a story line by its `#id:` key and re-interpolate Ink temp
     * vars from the EN template + the Ink-rendered text. Use this for any
     * narrative line / choice / dialogue card that has a `lineId` and an
     * originally-rendered English text.
     *
     * - On `en` locale: returns `originalText` unchanged.
     * - If no translation found for `lineId`: returns `originalText`.
     * - Otherwise: walks the EN template alongside `originalText` to recover
     *   `~ temp` var values + conditional truthiness, then interpolates the
     *   translated string with that ctx.
     *
     * @param {string} lineId          the Ink `#id:` value
     * @param {string} originalText    what Ink rendered in English (already interpolated)
     * @returns {string}
     */
    translateLine(lineId, originalText) {
        if (!lineId || this._lang === 'en') return originalText;
        const translated = this.get(lineId);
        if (!translated || translated === lineId) return originalText;
        const enMap = this._data.get('en');
        const enTpl = enMap?.get(lineId);
        const ctx = enTpl ? _extractInkTempVars(enTpl, originalText) : null;
        return this.interpolate(translated, ctx);
    }

    /** Set active language and load its TSV. */
    async setLanguage(lang) {
        this._lang = lang;
        try { localStorage.setItem(LANG_KEY, lang); } catch(e) {}

        if (lang !== 'en' && !this._data.has(lang)) {
            await this._fetchTSV(lang);
        }

        events.emit(Events.LOCALE_CHANGED || 'locale:changed', lang);
    }

    getLanguage() { return this._lang; }

    // SINGLE SOURCE OF TRUTH for every language picker (main-menu + pause-menu
    // Settings dropdowns AND the first-run flag popup). settings.js
    // populateLanguageControls() builds all three from this list — do NOT
    // hardcode <option>/popup-button lists in index.html again. Adding a
    // language = add ONE row here (+ its <code>.tsv + build/sheet wiring in
    // .claude/rules/l10n.md). `flag` is the emoji used by the first-run popup.
    // (2026-07-04: Italian shipped translated but was hardcoded out of both
    // dropdowns for ~2 months — this list now drives them so it can't recur.)
    getAvailableLanguages() {
        return [
            { code: 'en', name: 'English',  flag: '🇬🇧' },
            { code: 'de', name: 'Deutsch',  flag: '🇩🇪' },
            { code: 'fr', name: 'Français', flag: '🇫🇷' },
            { code: 'tr', name: 'Türkçe',   flag: '🇹🇷' },
            { code: 'es', name: 'Español',  flag: '🇪🇸' },
            { code: 'pt', name: 'Português', flag: '🇵🇹' },
            { code: 'zh', name: '中文',      flag: '🇨🇳' },
            { code: 'ru', name: 'Русский',  flag: '🇷🇺' },
            { code: 'th', name: 'ไทย',       flag: '🇹🇭' },
            { code: 'ko', name: '한국어',     flag: '🇰🇷' },
            { code: 'ja', name: '日本語',     flag: '🇯🇵' },
            { code: 'it', name: 'Italiano', flag: '🇮🇹' },
        ];
    }

    /**
     * Load TSV data for a language.
     * @param {string} lang - language code
     * @param {string} tsvText - raw TSV content
     */
    loadTSV(lang, tsvText) {
        const map = new Map();
        for (const line of tsvText.split('\n')) {
            if (!line || line.startsWith('#')) continue;
            // 2026-05-07: 3-column support for embedded dirty-toggle in en.tsv.
            // Format: key<TAB>text<TAB>dirty?
            //   - 2-col line: key + text (clean)
            //   - 3-col line: key + text + "1" (dirty, needs re-translation)
            //   - The dirty flag is metadata only; runtime ignores it and uses
            //     the text as-is. Sub-agent fan-out reads dirty flag from en.tsv.
            const parts = line.split('\t');
            if (parts.length < 2) continue;
            const key = parts[0].trim();
            const value = (parts[1] || '').trim().replace(/\\n/g, '\n');
            if (key) {
                if (map.has(key)) console.warn(`[L10n] Duplicate key in ${lang}: '${key}'`);
                map.set(key, value);
            }
        }
        this._data.set(lang, map);
    }

    /** @private Fetch and parse a TSV file. */
    async _fetchTSV(lang) {
        // Edge-retry the TSV fetch (3 attempts, cache:'reload' on retries) —
        // the Free build is served from itch.io's BunnyCDN, whose edge
        // intermittently 403s/drops a single asset request that succeeds on
        // retry. This was the last un-hardened sibling of the documented
        // bare-fetch class (story.json + items.json + tar + manifest already
        // retry; en.tsv was still a single bare fetch). A 403 here is
        // non-fatal — missing keys fall through to the English/baked-in
        // COMMON_TRANSLATIONS — but a transient edge 403 should not silently
        // blank a whole locale. foundby 0.2.8.3 Free (intake 2026-06-16).
        try {
            const resp = await fetchWithEdgeRetry(`${TSV_BASE}${lang}.tsv?v=${Date.now()}`);
            const text = await resp.text();
            this.loadTSV(lang, text);
        } catch (e) {
            console.warn(`[L10n] Failed to load ${lang}.tsv:`, e?.message || e);
        }
    }
}

export const L = new Localization();
